// Typed barrel over import.meta.env. Feature code never reads
// import.meta.env directly (per docs/architecture/frontend-structure.md §9).

// LogLevel typing comes from the shared observability package so env and the
// logger agree on the level union.
import { LOG_LEVELS, type LogLevel } from '@neon-one/frontend-observability';

const isLogLevel = (value: string): value is LogLevel =>
  (LOG_LEVELS as readonly string[]).includes(value);
export const env = {
  baseUrl: import.meta.env.BASE_URL,
  apiBaseUrl: (import.meta.env.VITE_API_BASE_URL ?? '/api/v1').replace(/\/+$/, ''),
  wsBaseUrl: (import.meta.env.VITE_WS_BASE_URL ?? (() => {
    const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${proto}//${window.location.host}/v1`;
  })()).replace(/\/+$/, ''),
  bidderBaseUrl: (import.meta.env.VITE_BIDDER_BASE_URL ?? 'http://localhost:5175').replace(/\/+$/, ''),
  neonSsoUrl: import.meta.env.VITE_NEON_SSO_URL ?? 'https://sandbox.neonsso.com',
  neonSsoPath: import.meta.env.VITE_NEON_SSO_URL_PATH ?? '/login',
  neonSsoClientId: import.meta.env.VITE_NEON_SSO_CLIENT_ID ?? '',
  ssoCallbackOrigin: (() => {
    const raw = import.meta.env.VITE_SSO_CALLBACK_ORIGIN ?? '';
    if (!raw) return '';
    try { return new URL(raw).origin; } catch { return ''; }
  })(),
  mockSsoEnabled: import.meta.env.VITE_MOCK_SSO_ENABLED === 'true',
  clientErrorReporting: import.meta.env.VITE_CLIENT_ERROR_REPORTING !== 'false',
  // Opt-in, so a build path that forgets the flag ships without it. Always on
  // under `vite dev`. Only the dev and uat deploy workflows set it to 'true'.
  feedbackWidget: import.meta.env.DEV || import.meta.env.VITE_FEEDBACK_WIDGET === 'true',
  // Pendo product analytics (GT1-208). An empty key disables Pendo entirely
  // (the dev/local default) — nothing is injected. Trimmed because an unset
  // build-time var can resolve to a blank string, the same guard
  // loadNeonPayScript applies to its CDN override.
  pendoApiKey: (import.meta.env.VITE_PENDO_API_KEY ?? '').trim(),
  // Namespaces non-prod Pendo visitor/account ids and rides along as record
  // metadata. 'prod' is the only value that yields un-prefixed ids. Blank or
  // whitespace-only build values fall back to 'local' like an unset var.
  pendoEnvironment: (() => {
    const raw = (import.meta.env.VITE_PENDO_ENVIRONMENT ?? '').trim().toLowerCase();
    return raw || 'local';
  })(),
  // Frontend log verbosity (GT1-414). Quieter INFO for production builds,
  // verbose DEBUG in dev; override per stage with VITE_LOG_LEVEL.
  // Typed as the LogLevel union so consumers never need casts.
  logLevel: ((): LogLevel => {
    const raw = (import.meta.env.VITE_LOG_LEVEL ?? '').toLowerCase();
    if (isLogLevel(raw)) return raw;
    return import.meta.env.PROD ? 'info' : 'debug';
  })(),
} as const;

export type Env = typeof env;
